/**
 * Activity Formatter Utility
 * Provides functions to parse and format activity content for display
 */

/**
 * Parse HTML content and convert to readable text
 * @param {string} htmlContent - HTML string to parse
 * @returns {object} - Formatted content object with title and body
 */
export const parseActivityContent = (htmlContent) => {
  if (!htmlContent) {
    return { title: '', body: '', type: 'text' };
  }

  // Check if content is HTML
  const isHtml = /<[^>]*>/.test(htmlContent);
  
  if (!isHtml) {
    // Plain text content
    return {
      title: '',
      body: htmlContent,
      type: 'text'
    };
  }

  // Sanitize HTML: remove all img tags for now
  let sanitizedHtml = htmlContent;
  
  // Remove all img tags to prevent any loading issues
  sanitizedHtml = sanitizedHtml.replace(/<img[^>]*>/gi, '');
  
  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = sanitizedHtml;

  // Extract and clean the content
  const extractedContent = {
    title: '',
    body: '',
    type: 'html',
    sections: []
  };

  // Try to find a greeting or title
  const firstLine = tempDiv.textContent.trim().split('\n')[0];
  if (firstLine && firstLine.startsWith('Dear ')) {
    extractedContent.title = firstLine;
  }

  // Extract main content sections
  const paragraphs = tempDiv.querySelectorAll('p, div');
  if (paragraphs.length === 0) {
    // If no paragraphs, try to parse text content
    const lines = tempDiv.innerHTML
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .split('\n')
      .filter(line => line.trim());

    lines.forEach(line => {
      const trimmedLine = line.trim();
      if (trimmedLine) {
        // Check for special content types
        if (trimmedLine.includes('Special Offer:') || trimmedLine.includes('Late Arrival Policy:')) {
          extractedContent.sections.push({
            type: 'important',
            content: trimmedLine
          });
        } else if (trimmedLine.includes('Location:')) {
          extractedContent.sections.push({
            type: 'location',
            content: trimmedLine
          });
        } else if (trimmedLine.match(/scheduled for|appointment/i)) {
          extractedContent.sections.push({
            type: 'appointment',
            content: trimmedLine
          });
        } else {
          extractedContent.sections.push({
            type: 'text',
            content: trimmedLine
          });
        }
      }
    });
  }

  // Extract links
  const links = tempDiv.querySelectorAll('a');
  const extractedLinks = [];
  links.forEach(link => {
    extractedLinks.push({
      text: link.textContent,
      href: link.href || link.getAttribute('href')
    });
  });

  if (extractedLinks.length > 0) {
    extractedContent.links = extractedLinks;
  }

  // Extract images
  const images = tempDiv.querySelectorAll('img');
  const extractedImages = [];
  images.forEach(img => {
    extractedImages.push({
      src: img.src || img.getAttribute('src'),
      alt: img.alt || 'Activity image',
      width: img.width || img.getAttribute('width'),
      height: img.height || img.getAttribute('height')
    });
  });

  if (extractedImages.length > 0) {
    extractedContent.images = extractedImages;
  }

  // Build formatted body
  extractedContent.body = tempDiv.textContent
    .replace(/\s+/g, ' ')
    .trim()
    .substring(0, 500); // Limit to 500 chars for preview

  return extractedContent;
};

/**
 * Format activity type for display
 * @param {string} type - Activity type
 * @returns {string} - Formatted type label
 */
export const formatActivityType = (type) => {
  const typeMap = {
    'task': 'Task',
    'call': 'Phone Call',
    'email': 'Email',
    'appointment': 'Appointment',
    'payment': 'Payment',
    'assignment': 'Assignment',
    'note': 'Note',
    'reminder': 'Reminder',
    'follow_up': 'Follow Up',
    'marketing': 'Marketing'
  };

  return typeMap[type?.toLowerCase()] || type || 'Activity';
};

/**
 * Format priority level for display
 * @param {string} priority - Priority level
 * @returns {object} - Priority display object with label and color
 */
export const formatPriority = (priority) => {
  const priorityMap = {
    'high': { label: 'High Priority', color: 'error' },
    'medium': { label: 'Medium Priority', color: 'warning' },
    'low': { label: 'Low Priority', color: 'info' },
    'urgent': { label: 'Urgent', color: 'error' }
  };

  return priorityMap[priority?.toLowerCase()] || { label: priority || 'Normal', color: 'default' };
};

/**
 * Format status for display
 * @param {string} status - Activity status
 * @returns {object} - Status display object with label and color
 */
export const formatStatus = (status) => {
  const statusMap = {
    'pending': { label: 'Pending', color: 'warning' },
    'completed': { label: 'Completed', color: 'success' },
    'scheduled': { label: 'Scheduled', color: 'info' },
    'overdue': { label: 'Overdue', color: 'error' },
    'cancelled': { label: 'Cancelled', color: 'default' },
    'in_progress': { label: 'In Progress', color: 'primary' }
  };

  return statusMap[status?.toLowerCase()] || { label: status || 'Unknown', color: 'default' };
};

/**
 * Extract key information from activity notes
 * @param {string} notes - Activity notes content
 * @returns {object} - Extracted information
 */
export const extractActivityInfo = (notes) => {
  const info = {
    clientName: null,
    appointmentDate: null,
    location: null,
    specialOffers: [],
    policies: [],
    links: []
  };

  if (!notes) return info;

  // Extract client name from "Dear [Name]" pattern
  const clientMatch = notes.match(/Dear\s+([^,\n]+)/i);
  if (clientMatch) {
    info.clientName = clientMatch[1].trim();
  }

  // Extract appointment date/time
  const dateMatch = notes.match(/scheduled for\s+([^.]+)/i);
  if (dateMatch) {
    info.appointmentDate = dateMatch[1].trim();
  }

  // Extract location
  const locationMatch = notes.match(/Location:\s*([^\n]+)/i);
  if (locationMatch) {
    info.location = locationMatch[1].trim();
  }

  // Extract special offers
  const offerMatch = notes.match(/Special Offer:\s*([^\n]+)/i);
  if (offerMatch) {
    info.specialOffers.push(offerMatch[1].trim());
  }

  // Extract policies
  const policyMatch = notes.match(/Policy:\s*([^\n]+)/i);
  if (policyMatch) {
    info.policies.push(policyMatch[1].trim());
  }

  return info;
};

/**
 * Truncate text to specified length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} - Truncated text
 */
export const truncateText = (text, maxLength = 150) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};